import React, { useState } from 'react'
import ReactCrop, {
  centerCrop,
  convertToPixelCrop,
  makeAspectCrop
} from 'react-image-crop'
import 'react-image-crop/dist/ReactCrop.css'
import PropTypes from 'prop-types'
import { postRequest, successNotification, carsUrl, errorNotification } from '../../config'
import { Button, Col, Space } from 'antd'
import { ZoomInOutlined, ZoomOutOutlined } from '@ant-design/icons'

// The shapes worth offering, plus the whole picture. Dragging a selection out of nothing is the
// hardest thing to do on a phone, so one tap puts a box on the image and the handles only have to
// be nudged from there.
const RATIOS = [
  { label: '16:9', value: 16 / 9 },
  { label: '4:3', value: 4 / 3 },
  { label: 'Square', value: 1 },
  { label: 'Free', value: null }
]

const CropImage = props => {
  const { currentImage, handleSuccess } = props
  // eslint-disable-next-line
  const [payload, setPayload] = useState({
    active: currentImage.active,
    mainImage: currentImage.mainImage,
    isShowcaseImage: currentImage.isShowcaseImage,
    isVideo: currentImage.isVideo,
    videoId: currentImage.videoId
  })
  const [croppedImage, setCroppedImage] = useState(null)
  // `{ aspect: 16 / 9 }` used to live here. That is the version-8 API: in react-image-crop 11
  // (what package.json pins) `aspect` is a prop on <ReactCrop> and a crop needs a unit and a size.
  // The upshot was no starting box and no 16:9 lock — you had to free-drag a selection out of
  // nothing, which is miserable on a phone and the main reason this felt broken there.
  const [crop, setCrop] = useState()
  const [aspect, setAspect] = useState(16 / 9)
  const [image, setImage] = useState(null)
  // eslint-disable-next-line
  const [serverErrors, setServerErrors] = useState({})
  const [loading, setLoading] = useState(false)
  const [scale, setScale] = useState(1)

  // A centred box covering most of the picture, in the chosen shape.
  const initialCrop = (width, height, ratio) =>
    centerCrop(
      ratio
        ? makeAspectCrop({ unit: '%', width: 80 }, ratio, width, height)
        : { unit: '%', width: 80, height: 80, x: 0, y: 0 },
      width,
      height
    )

  const onImageLoaded = e => {
    const img = e.currentTarget
    setImage(img)
    startCropAt(img, aspect)
  }

  const chooseAspect = ratio => {
    setAspect(ratio)
    if (image) startCropAt(image, ratio)
  }

  const onCropChange = crop => {
    // console.log('on crop change ', crop)
    setCrop(crop)
  }

  // Rasterise `pixelCrop` out of `img`. Split from the event handler so the initial box can be
  // rendered too: react-image-crop only fires onComplete after a drag, so a crop that is on screen
  // from the start would otherwise have nothing behind it and Submit would refuse it.
  const renderCrop = (img, pixelCrop) => {
    if (!img || !pixelCrop || !pixelCrop.width || !pixelCrop.height) return
    const canvas = document.createElement('canvas')
    const scaleX = img.naturalWidth / img.width
    const scaleY = img.naturalHeight / img.height
    canvas.width = pixelCrop.width * scaleX
    canvas.height = pixelCrop.height * scaleY
    const ctx = canvas.getContext('2d')
    ctx.drawImage(
      img,
      pixelCrop.x * scaleX,
      pixelCrop.y * scaleY,
      pixelCrop.width * scaleX,
      pixelCrop.height * scaleY,
      0,
      0,
      pixelCrop.width * scaleX,
      pixelCrop.height * scaleY
    )
    try {
      setCroppedImage(canvas.toDataURL('image/jpeg'))
    } catch (err) {
      // A canvas holding a cross-origin image cannot be exported.
      errorNotification('This photo cannot be cropped here (its host does not allow it)')
    }
  }

  const onCropComplete = pixelCrop => renderCrop(image, pixelCrop)

  // Put a box on the picture and render it, so Submit works without a drag.
  const startCropAt = (img, ratio) => {
    const next = initialCrop(img.width, img.height, ratio)
    setCrop(next)
    renderCrop(img, convertToPixelCrop(next, img.width, img.height))
  }

  const handleSubmit = async event => {
    event.preventDefault()
    if (!croppedImage) {
      // It used to return in silence here, so tapping Submit before a crop had registered simply
      // did nothing and said nothing.
      errorNotification('Drag a selection on the photo first')
      return
    }

    // Prepare FormData
    const formData = new FormData()
    formData.append('image', croppedImage ? croppedImage : '')
    formData.append('is_showcase_image', payload.isShowcaseImage)
    formData.append('active', payload.active)
    formData.append('image_id', currentImage.id)
    formData.append('is_video', payload.isVideo)
    formData.append('video_id', payload.videoId)

    const response = await postRequest(
      `${carsUrl}/update-image`,
      formData,
      'image',
      setLoading,
      false
    )
    if (response.success) {
      handleSuccess(response.data)
      successNotification('Image Updated')
    } else {
      if (response.error.response) {
        if (response.error.response) {
          setServerErrors(response.error.response.data)
        }
      }
    }
  }

  return (
    <div className="row image-cropper">
      <div className="col-lg-12">
        <div className="image-cropper__bar">
          <Space wrap>
            {RATIOS.map(ratio => (
              <Button
                key={ratio.label}
                size="large"
                type={aspect === ratio.value ? 'primary' : 'default'}
                onClick={() => chooseAspect(ratio.value)}
              >
                {ratio.label}
              </Button>
            ))}
          </Space>
          <Space>
            <Button
              size="large"
              icon={<ZoomOutOutlined />}
              onClick={() => setScale(s => Math.max(0.5, s - 0.1))}
              disabled={scale <= 0.5}
            />
            <span style={{ minWidth: '40px', textAlign: 'center' }}>
              {Math.round(scale * 100)}%
            </span>
            <Button
              size="large"
              icon={<ZoomInOutlined />}
              onClick={() => setScale(s => Math.min(3, s + 0.1))}
              disabled={scale >= 3}
            />
          </Space>
        </div>
        <div
          style={{
            overflow: 'auto',
            maxHeight: '60vh',
            display: 'flex',
            justifyContent: 'center'
          }}
        >
          {/* Zoom by sizing the image, not by CSS-transforming a wrapper around it. A transform
              leaves the element's layout box at its old size, so react-image-crop measured one
              thing while the eye saw another and the crop box drifted away from the finger at any
              scale but 100%. It also broke the scroll area, since a transform does not grow it. */}
          <ReactCrop
            crop={crop}
            aspect={aspect || undefined}
            onChange={onCropChange}
            onComplete={onCropComplete}
          >
            <img
              src={props.currentImage.image}
              onLoad={onImageLoaded}
              crossOrigin="anonymous"
              style={{ width: `${scale * 100}%`, maxWidth: 'none' }}
              alt=""
            />
          </ReactCrop>
        </div>
        <Col md={24}>
          <div
            style={{
              display: 'flex',
              flexDirection: 'row',
              justifyContent: 'flex-end',
              marginBottom: '20px',
              marginTop: '10px'
            }}
          >
            <Space>
              <Button
                type="primary"
                size="large"
                onClick={handleSubmit}
                loading={loading}
              >
                Submit
              </Button>
            </Space>
          </div>
        </Col>
      </div>
    </div>
  )
}

CropImage.propTypes = {
  currentImage: PropTypes.any.isRequired,
  handleSuccess: PropTypes.func.isRequired
}

export default CropImage
